/**
 * Text formatting for the station services panels and the relink dialogs (M3 plan R16). Locale-free, so the
 * panels read the same everywhere and the tests are stable. UI-time code, never per frame.
 */
import type { StationType } from '../../../galaxy/layout/types'
import type { PartId } from '../../types'

/** A whole number with thin-space-free comma grouping: 12500 → "12,500". Negative numbers keep their sign. */
export function groupDigits(n: number): string {
  if (!Number.isFinite(n)) return '—'
  const whole = Math.round(n)
  const sign = whole < 0 ? '-' : ''
  const digits = String(Math.abs(whole))
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ','
    out += digits[i]
  }
  return sign + out
}

/** Credits: 12500 → "12,500 cr". */
export function formatCredits(n: number): string {
  return `${groupDigits(n)} cr`
}

/** Tonnes, to one decimal only when needed: 12 → "12 t", 2.5 → "2.5 t". */
export function formatTonnes(t: number): string {
  if (!Number.isFinite(t)) return '—'
  const tenths = Math.round(t * 10) / 10
  return Number.isInteger(tenths) ? `${groupDigits(tenths)} t` : `${tenths.toFixed(1)} t`
}

/** A scanner range: metres → "50,000 km"; null = the whole system; undefined = not known yet. */
export function formatRange(m: number | null | undefined): string {
  if (m === undefined) return '—'
  if (m === null) return 'Whole system'
  return `${groupDigits(m / 1000)} km`
}

/** Seconds as m:ss: 210 → "3:30", 59.6 → "1:00". */
export function formatMmSs(seconds: number): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s < 10 ? '0' : ''}${s}`
}

const MK_NUMERALS: Readonly<Record<1 | 2 | 3, string>> = { 1: 'I', 2: 'II', 3: 'III' }

/** "Mk I", "Mk II", "Mk III". */
export function formatMk(mk: 1 | 2 | 3): string {
  return `Mk ${MK_NUMERALS[mk]}`
}

const PART_LABELS: Readonly<Record<PartId, string>> = { hull_plating: 'Hull plating', electronics: 'Electronics', drive_parts: 'Drive parts' }

export function partLabel(part: PartId): string {
  return PART_LABELS[part]
}

/** "trade-post" → "Trade post". */
export function stationTypeLabel(type: StationType): string {
  const words = type.replace(/-/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
